import { PureComponent } from 'react'
import * as cn from 'classnames'
import * as moment from 'moment'
import * as styles from 'styles/atoms/charts/MoneyflowTrackerChart.css'
import { ExtendedMoneyflowTracker } from 'interfaces/moneyflow'
import { formatUSCurrency } from 'helpers/formatHelpers'
import Tooltip, { TooltipTheme } from 'components/features/pfm/Tooltip/Tooltip'
import { MONEYFLOW_LIMIT_TYPE } from 'constants/moneyflow'
import Touchable from 'components/utils/Touchable'
import { OuterEventTrackerContext, OuterEventTrackerContextType } from 'components/utils/OuterEventTracker'
import { createIsAnyPropChanged } from 'helpers/utilsHelpers'
import withContext from 'hocs/withContext/withContext'

type MoneyflowTrackerChartProps = ExtendedMoneyflowTracker & {
  outerEventTracker: OuterEventTrackerContextType
}

type ShownTooltip = 'current' | 'projection' | 'limit'
type MoneyflowTrackerChartState = {
  shownTooltip: null | ShownTooltip
  labelOffsetAmount: number
  that: MoneyflowTrackerChart
}

const calculatePercentage = (total: number, amount: number) =>
  total === amount ? 100 : (amount * 100) / total

// at the same time it's the most right position; percents
const DEFAULT_LIMIT_POSITION = 75
const MIN_BAR_TOTAL = 1

const isChartChanged = createIsAnyPropChanged<ExtendedMoneyflowTracker>('current', 'projection', 'limit')

class MoneyflowTrackerChart extends PureComponent<MoneyflowTrackerChartProps, MoneyflowTrackerChartState> {
  static defaultProps: Partial<MoneyflowTrackerChartProps> = {
    projection: 0,
  }

  static getDerivedStateFromProps (nextProps: MoneyflowTrackerChartProps, prevState: MoneyflowTrackerChartState) {
    if (nextProps !== prevState.that.props) {
      return {}
    }

    return { labelOffsetAmount: 0 }
  }

  state: MoneyflowTrackerChartState = {
    labelOffsetAmount: 0,
    shownTooltip: null,
    that: this,
  }

  private rootNode: HTMLElement | null = null
  private limitNode: HTMLSpanElement | null = null
  private labelNode: HTMLSpanElement | null = null
  private currentBarNode: HTMLDivElement | null = null
  private projectionBarNode: HTMLDivElement | null = null

  render () {
    const { current, limit, severity, type, projection } = this.props
    const { labelOffsetAmount } = this.state

    // TODO - combine with moneyflow breakdown limit position
    // default scale is the scale when limit at its default position
    // but scale may be increased if projected doesn't fit
    const barTotalByLimit = (limit * 100) / DEFAULT_LIMIT_POSITION
    const barTotal = Math.max(projection, barTotalByLimit, MIN_BAR_TOTAL)
    const limitPosition = limit === 0
      ? '0%'
      : (
        projection > barTotalByLimit
          ? ((limit * 100) / projection) + '%'
          : DEFAULT_LIMIT_POSITION + '%'
      )

    const projectionBarWidth = current !== projection ? calculatePercentage(barTotal, projection) : 0
    const actualBarWidth = calculatePercentage(barTotal, current)
    const adjustAmount = (projectionBarWidth - actualBarWidth) / 2
    const projectionTooltipPosition = calculatePercentage(projectionBarWidth, (projectionBarWidth - adjustAmount))
    const actualTooltipPosition = calculatePercentage(actualBarWidth, (actualBarWidth - (actualBarWidth / 2)))

    const currentBarClasses = cn(
      styles.moneyflowBar,
      styles.current, {
        [styles.over]: severity === 'danger',
      }
    )
    const projectionBarClasses = cn(
      styles.moneyflowBar,
      styles.projection, {
        [styles.over]: severity === 'danger',
        [styles.warning]: severity === 'warning',
      }
    )

    const shiftLabelLeft = current < (.1 * barTotal) || current === 0
    const translateAmount = shiftLabelLeft ? labelOffsetAmount : -labelOffsetAmount
    const labelStyle = { transform: `translateX(${ translateAmount }px)` }
    const currentMonth = moment().format('MMMM')

    const labelClass = cn(styles.value, {
      [styles.outerLabel]: shiftLabelLeft,
      [styles.negativeValue]: current < 0,
    })

    const dollarLabel = (
      <span ref={ (node) => this.labelNode = node } style={ labelStyle } className={ labelClass }>
        { formatUSCurrency(current) }
      </span>
    )

    const projectionTooltipColor: TooltipTheme =
      severity === 'danger' || severity === 'warning' ? severity : 'member'
    const actualTooltipColor: TooltipTheme = severity === 'danger' ? 'danger' : 'member'

    const projectionTooltip = (
      <div style={{ left: projectionTooltipPosition + '%' }} className={ styles.tooltipContainer }>
        <Tooltip theme={ projectionTooltipColor } light>
          { formatUSCurrency(projection) } projected in { currentMonth }
        </Tooltip>
      </div>
    )

    const actualTooltip = current > limit ? (
      <div style={{ left: actualTooltipPosition + '%' }} className={ styles.tooltipContainer }>
        <Tooltip theme={ actualTooltipColor }>
          { formatUSCurrency(current - limit) } over limit
        </Tooltip>
      </div>
    ) : null

    const limitTooltip = (
      <div className={ styles.tooltipContainer }>
        <Tooltip theme='member'>{ MONEYFLOW_LIMIT_TYPE[type] }: { formatUSCurrency(limit) }</Tooltip>
      </div>
    )

    const { shownTooltip } = this.state

    return (
      <Touchable
        childRef={ (node: HTMLElement) => this.rootNode = node }
        onTap={ this.onTap }
        onMouseLeave={ this.hideTooltip }
      >
        <figure className={ styles.moneyflowTrackerChart } data-shown-tooltip={ shownTooltip }>
          <div
            ref={ (node) => this.projectionBarNode = node }
            className={ projectionBarClasses }
            style={{ width: projectionBarWidth + '%' }}
            onMouseEnter={ () => this.setState({ shownTooltip : 'projection' }) }
          >
            { projectionTooltip }
          </div>
          <div
            ref={ (node) => this.currentBarNode = node }
            className={ currentBarClasses }
            style={{ width: actualBarWidth + '%' }}
            onMouseEnter={ () => this.setState({ shownTooltip : 'current' }) }
          >
            { actualTooltip }
            { dollarLabel }
          </div>
          <div
            ref={ (node) => this.limitNode = node }
            className={ styles.limit }
            style={{ left: limitPosition }}
            onMouseEnter={ () => this.setState({ shownTooltip : 'limit' }) }
          >
            <div className={ styles.limitDot } />
            { limitTooltip }
          </div>
        </figure>
      </Touchable>
    )
  }

  componentDidMount () {
    this.handleCollision()
    this.props.outerEventTracker.addOuterEventListener('tap', this.hideTooltip, this.rootNode)
  }

  componentDidUpdate (prevProps: MoneyflowTrackerChartProps) {
    if (isChartChanged(prevProps, this.props)) {
      this.handleCollision()
    }
  }

  componentWillUnmount () {
    this.props.outerEventTracker.removeOuterEventListener('tap', this.hideTooltip)
  }

  private getShownTooltip = (node: HTMLElement): null | ShownTooltip => {
    let shownTooltip: null | ShownTooltip = null
    if (this.currentBarNode && this.currentBarNode.contains(node)) {
      shownTooltip = 'current'
    }
    if (this.projectionBarNode && this.projectionBarNode.contains(node)) {
      shownTooltip = 'projection'
    }
    if (this.limitNode && this.limitNode.contains(node)) {
      shownTooltip = 'limit'
    }

    return shownTooltip
  }

  private hideTooltip = () => {
    this.setState({ shownTooltip: null })
  }

  private handleCollision () {
    if (this.limitNode && this.labelNode) {
      const limitRect = this.limitNode.getBoundingClientRect()
      const labelRect = this.labelNode.getBoundingClientRect()
      const collision = limitRect.left < labelRect.left + labelRect.width &&
                        limitRect.left + limitRect.width > labelRect.left
      if (collision) {
        const PADDING = 8
        const labelOffsetAmount = Math.abs(labelRect.right - limitRect.left) + PADDING
        this.setState({ labelOffsetAmount })
      } else {
        this.setState({ labelOffsetAmount: 0 })
      }
    }
  }

  private onTap: HammerListener = (e) => {
    const { target } = e
    const shownTooltip = this.getShownTooltip(target)

    this.setState({ shownTooltip })
  }
}

export default withContext(OuterEventTrackerContext, 'outerEventTracker')(MoneyflowTrackerChart)
